import {
  defaultAccountOfKind,
  defaultVoucherDate,
  entryAccounts,
  splitPostingAccount,
} from './reference-data.js';
import type { VoucherTypeCode, VoucherTypeConfig } from './voucher-config.js';
import {
  emptyLine,
  summarise,
  toPayload,
  validate,
  type Currency,
  type FxContext,
  type Role,
  type Validation,
  type VoucherDraft,
  type VoucherPayload,
  type VoucherSummary,
} from './voucher-model.js';
import { fromExcelSerial, looksLikeExcelSerial, readXlsx } from './xlsx.js';



const COLUMNS = {
  reference: ['voucher ref', 'voucher', 'ref', 'reference', 'voucher no', 'group'],
  date: ['date', 'voucher date', 'txn date'],
  contraAccount: [
    'cash/bank account',
    'cash account',
    'bank account',
    'contra account',
    'paid from',
    'received into',
  ],
  party: ['party', 'party code', 'customer', 'supplier'],
  narration: ['narration', 'voucher narration', 'description', 'particulars'],
  account: ['account', 'account code', 'gl account', 'code'],
  amount: ['amount', 'payment', 'receipt', 'value'],
  debit: ['debit', 'dr'],
  credit: ['credit', 'cr'],
  lineNarration: ['line narration', 'line description', 'remarks', 'detail'],
  currency: ['currency', 'ccy'],
  fxRate: ['fx rate', 'rate', 'exchange rate'],
} as const;

type ColumnKey = keyof typeof COLUMNS;

function normaliseHeader(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, ' ')
    .replace(/\.$/, '');
}

function mapHeaders(header: string[]): Partial<Record<ColumnKey, number>> {
  const map: Partial<Record<ColumnKey, number>> = {};
  header.forEach((raw, index) => {
    const name = normaliseHeader(raw);
    for (const [key, aliases] of Object.entries(COLUMNS) as Array<[ColumnKey, readonly string[]]>) {
      if (map[key] === undefined && aliases.includes(name)) map[key] = index;
    }
  });
  return map;
}


export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  const input = text.replace(/^﻿/, '');

  for (let i = 0; i < input.length; i++) {
    const character = input[i];

    if (quoted) {
      if (character === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += character;
      }
      continue;
    }

    if (character === '"') {
      quoted = true;
    } else if (character === ',' || character === ';' || character === '\t') {
      row.push(field);
      field = '';
    } else if (character === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (character !== '\r') {
      field += character;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows.map((r) => r.map((cell) => cell.trim()));
}

export async function readSpreadsheet(file: File): Promise<string[][]> {
  const isXlsx =
    file.name.toLowerCase().endsWith('.xlsx') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  if (isXlsx) return readXlsx(file);

  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  if (head[0] === 0xd0 && head[1] === 0xcf) {
    throw new Error(
      'That is a legacy .xls file. Open it in Excel and save as .xlsx or CSV, then upload again.',
    );
  }
  if (head[0] === 0x50 && head[1] === 0x4b) return readXlsx(file);

  return parseCsv(await file.text());
}


export interface BulkIssue {
  row: number;
  message: string;
}

export interface ParsedVoucher {
  reference: string;
  row: number;
  draft: VoucherDraft;
  fx: FxContext;
  summary: VoucherSummary;
  validation: Validation;
  payload: VoucherPayload;
}

export interface BulkParse {
  vouchers: ParsedVoucher[];
  issues: BulkIssue[];
}

export interface BulkDefaults {
  currency: Currency;
  fxRate: string;
  baseCurrency: Currency;
  branchCode: string | null;
  role: Role;
}

interface RawLine {
  row: number;
  account: string;
  debit: string;
  credit: string;
  narration: string;
  party: string;
}

interface Group {
  reference: string;
  row: number;
  date: string;
  narration: string;
  contraAccount: string;
  currency: Currency;
  fxRate: string;
  lines: RawLine[];
}

function cleanAmount(raw: string): string {
  return raw.replace(/[\s,]/g, '').replace(/^(PKR|USD|Rs\.?|\$)/i, '');
}

function normaliseDate(raw: string): string {
  const value = raw.trim();
  if (value === '') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (looksLikeExcelSerial(value)) return fromExcelSerial(Number(value));

  const slashed = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(value);
  if (slashed) {
    const [, day, month, year] = slashed as unknown as [string, string, string, string];
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return value;
}

export function parseRows(
  rows: string[][],
  type: VoucherTypeCode,
  config: VoucherTypeConfig,
  defaults: BulkDefaults,
): BulkParse {
  const issues: BulkIssue[] = [];

  const header = rows.find((row) => row.some((cell) => cell !== ''));
  if (!header) return { vouchers: [], issues: [{ row: 1, message: 'The file is empty.' }] };

  const headerRow = rows.indexOf(header);
  const columns = mapHeaders(header);

  if (columns.account === undefined) {
    return {
      vouchers: [],
      issues: [
        {
          row: headerRow + 1,
          message:
            'No Account column found. Download the template for this voucher type — the header row has to name the columns.',
        },
      ],
    };
  }

  const hasAmountColumn =
    columns.amount !== undefined || columns.debit !== undefined || columns.credit !== undefined;
  if (!hasAmountColumn) {
    return {
      vouchers: [],
      issues: [
        {
          row: headerRow + 1,
          message:
            config.shape === 'JOURNAL'
              ? 'No Debit or Credit column found.'
              : `No ${config.amountLabel} column found.`,
        },
      ],
    };
  }

  const at = (row: string[], key: ColumnKey): string => {
    const index = columns[key];
    return index === undefined ? '' : (row[index] ?? '').trim();
  };

  const groups = new Map<string, Group>();

  for (let i = headerRow + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.every((cell) => cell === '')) continue;
    const fileRow = i + 1;

    const account = at(row, 'account');
    const amount = cleanAmount(at(row, 'amount'));
    const debit = cleanAmount(at(row, 'debit'));
    const credit = cleanAmount(at(row, 'credit'));

    if (account === '' && amount === '' && debit === '' && credit === '') continue;

    const reference = at(row, 'reference') || `Row ${fileRow}`;

    let group = groups.get(reference);
    if (!group) {
      const currencyRaw = at(row, 'currency').toUpperCase();
      const currency: Currency = /^[A-Z]{3}$/.test(currencyRaw) ? currencyRaw : defaults.currency;
      const rate = cleanAmount(at(row, 'fxRate'));

      group = {
        reference,
        row: fileRow,
        date: normaliseDate(at(row, 'date')) || defaultVoucherDate(),
        narration: at(row, 'narration'),
        contraAccount: at(row, 'contraAccount'),
        currency,
        fxRate: currency === defaults.baseCurrency ? '1' : rate || defaults.fxRate,
        lines: [],
      };
      groups.set(reference, group);
    }

    if (account === '') {
      issues.push({ row: fileRow, message: 'Row has an amount but no account — skipped.' });
      continue;
    }

    let lineDebit = debit;
    let lineCredit = credit;
    if (config.shape !== 'JOURNAL' && amount !== '') {
      if (config.analysisSide === 'Dr') lineDebit = amount;
      else lineCredit = amount;
    }

    if (lineDebit === '' && lineCredit === '') {
      issues.push({ row: fileRow, message: `${account} has no amount — skipped.` });
      continue;
    }

    group.lines.push({
      row: fileRow,
      account,
      debit: lineDebit,
      credit: lineCredit,
      narration: at(row, 'lineNarration'),
      party: at(row, 'party') || group.lines[0]?.party || '',
    });
  }

  const vouchers: ParsedVoucher[] = [];

  for (const group of groups.values()) {
    if (group.lines.length === 0) continue;

    const draft: VoucherDraft = {
      voucherNo: null,
      voucherDate: group.date,
      narration: group.narration,
      contraAccountCode:
        group.contraAccount || (config.contra ? defaultAccountOfKind(config.contra.kind) : ''),
      lines: group.lines.map((line) => ({
        ...emptyLine(),
        ...splitPostingAccount(line.account),
        narration: line.narration,
        debit: line.debit,
        credit: line.credit,
        partyCode: line.party,
      })),
      status: 'NEW',
      approvalState: 'NOT_REQUIRED',
    };

    const fx: FxContext = {
      currency: group.currency,
      fxRate: group.fxRate,
      baseCurrency: defaults.baseCurrency,
    };
    const summary = summarise(draft, config, fx);
    const validation = validate(draft, config, summary, defaults.role, fx);

    vouchers.push({
      reference: group.reference,
      row: group.row,
      draft,
      fx,
      summary,
      validation,
      payload: toPayload(type, draft, summary, fx, defaults.branchCode),
    });
  }

  return { vouchers, issues };
}


export function templateCsv(type: VoucherTypeCode, config: VoucherTypeConfig): string {
  const amountHeader = config.shape === 'JOURNAL' ? ['Debit', 'Credit'] : [config.amountLabel];

  const header = [
    'Voucher ref',
    'Date',
    ...(config.contra ? ['Cash/Bank account'] : []),
    'Party',
    'Narration',
    'Account',
    ...amountHeader,
    'Line narration',
    'Currency',
    'FX rate',
  ];

  const today = defaultVoucherDate();
  const sample = (type: 'EXPENSE' | 'LIABILITY') =>
    entryAccounts().find((a) => a.type === type && a.isLeaf && a.allowDirectPosting)?.code ?? '';
  const examples: string[][] =
    config.shape === 'JOURNAL'
      ? [
          [
            'V1',
            today,
            '',
            'Month-end accrual',
            sample('EXPENSE'),
            '25000.00',
            '',
            'Freight accrual',
            'PKR',
            '1',
          ],
          [
            'V1',
            today,
            '',
            'Month-end accrual',
            sample('LIABILITY'),
            '',
            '25000.00',
            'Accrued expenses',
            'PKR',
            '1',
          ],
        ]
      : [
          [
            'V1',
            today,
            '',
            '',
            config.narrationPlaceholder,
            '',
            '15000.00',
            'First line',
            'PKR',
            '1',
          ],
        ];

  const rows = config.contra
    ? examples
    : examples.map((row) => [...row.slice(0, 2), ...row.slice(3)]);

  const escape = (cell: string) =>
    /[",;\t\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;

  const lines = [header, ...rows].map((row) => row.map(escape).join(','));

  return [
    ...lines,
    '',
    `# ${config.label} (${type}) — one row per line, grouped by Voucher ref.`,
    '# Date accepts 2026-07-30 or 30/07/2026. Amounts are plain numbers; commas are ignored.',
    ...(config.contra
      ? [
          `# Cash/Bank account is the ${config.contra.label.toLowerCase()} account — same on every row of a voucher.`,
        ]
      : []),
    '# Currency and FX rate are optional; blank uses the top bar. Delete these comment lines before uploading.',
  ].join('\r\n');
}

export function downloadText(
  filename: string,
  text: string,
  mime = 'text/csv;charset=utf-8',
): void {
  const blob = new Blob([`﻿${text}`], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
